import Phaser from 'phaser';
import i18next from 'i18next';
import { demoRunsToSaturn } from '@/game/clip';
import { getActivePack, packKey } from '@/game/packs';
import { APP_STORE_URL, playStoreUrl } from '@/game/promo';
import { showPromoCard } from '@/game/ui/promoCard';
import { storeTarget, type StoreTarget } from '@/game/storeTarget';
import { trackStoreClick, trackTrialSpent } from '@/analytics';

/**
 * The web demo's end-of-run card, for a visitor whose store we know (src/game/clip.ts
 * `demoRunsToSaturn`).
 *
 * It is the counterpart of `clipOutro.ts`, and the two differ in exactly the way their endings do:
 *
 *  - `clipOutro` ends a **board allowance**. It cannot know the device, so it offers both badges
 *    and a way back into the game ("try another lesson"). It is an offer beside an open door.
 *  - this card ends a **run that reached Saturn**, on a device that can only buy in one place. The
 *    player has just been paid a 💎 Diamond, which is the best thing that has happened in the demo
 *    so far, and the next thing that happens is the ask. One destination, so one button — and the
 *    whole card is that button (`onCardClick`), because a card with a single answer should not
 *    make anyone hunt for it.
 *
 * The click LEAVES the page (`location.assign`), deliberately, rather than opening a tab: on a
 * phone that hands off to the App Store or Play app, which a popup — blocked as often as not
 * without a user gesture behind it — does not. Nothing here fires on its own: a player who taps
 * nothing stays on a paused game, which is the honest state of a demo that has run out.
 *
 * **What happens on the way out matters as much as the link.** On a phone the store URL is caught
 * by the App Store or Play *app*, so the browser never unloads — and a player who looks at the
 * listing and then switches back finds the card still standing over a frozen board, with the only
 * button being the one they already pressed. So the game goes back to its Menu in the same breath
 * as the navigation: `onLeave` runs immediately after `location.assign`, while the page is still
 * ours. If the URL opened in the browser instead, the page unloads and `onLeave` never matters.
 */
/**
  * Resolved at the moment of the click, not once at import: playStoreUrl() reads this page's own
  * UTMs so Play credits the install to the campaign that paid for it. A module-level constant would
  * have been frozen before the landing URL was ever parsed.
  */
function storeUrl(store: StoreTarget): string {
  return store === 'appstore' ? APP_STORE_URL : playStoreUrl();
}

/**
 * Show it if this really is a Saturn demo run. Every gate lives in here, like `maybeShowBuyPrompt`
 * and `maybeShowClipOutro` — a caller should never have to know the rules to ask the question.
 */
export interface DemoSaturnCardOptions {
  delayMs?: number;
  /**
   * Runs once the store has been opened: put the game back where a returning player should find
   * it. Supplied by the caller because the caller is what stopped the game — the HUD paused Board
   * and Round (HUDScene.endDemoAtSaturn), so the HUD is what un-pauses them.
   */
  onLeave?: () => void;
}

export function maybeShowDemoSaturnCard(
  scene: Phaser.Scene,
  opts: DemoSaturnCardOptions = {},
): boolean {
  if (!demoRunsToSaturn()) return false;
  const store = storeTarget();
  if (!store) return false;   // demoRunsToSaturn() already implies one; belt and braces for the type
  showDemoSaturnCard(scene, store, opts);
  return true;
}

export function showDemoSaturnCard(
  scene: Phaser.Scene,
  store: StoreTarget,
  opts: DemoSaturnCardOptions = {},
): void {
  // The demo's mid-funnel event: this lesson's trial is over and the sale card is up. Same event
  // the board-capped ending sends, so the two demo endings stay comparable in one funnel.
  trackTrialSpent(packKey(getActivePack()));

  let leaving = false;
  const go = (): void => {
    if (leaving) return;   // the button and the card both call this; the second must be a no-op
    leaving = true;
    trackStoreClick(store);
    window.location.assign(storeUrl(store));
    // Ordered, not incidental: the navigation is asked for first, inside the tap that authorised
    // it, and only then does the game tidy up behind it. The other way round the scene switch
    // would tear down the card — and with it the click still being dispatched.
    opts.onLeave?.();
  };

  showPromoCard(scene, {
    title: i18next.t('clip.saturnTitle', { planet: i18next.t('scoreboard.planets.saturn') }),
    lines: [
      i18next.t('clip.saturnBody'),
      i18next.t('buy.once'),
      i18next.t('buy.offline'),
    ],
    // keepOpen: the click navigates away, so there is nothing to close — and if the navigation is
    // somehow refused, a card still standing is better than a paused board with no way on.
    actions: [{ label: i18next.t('clip.saturnGo'), kind: 'primary', onClick: go, keepOpen: true }],
  }, { delayMs: opts.delayMs ?? 0, size: 'lg', onCardClick: go });
}
